/**
 * 深邃蓝模板 - VIP会员页
 * 设计：沉浸式暗色商务 + 权益矩阵 + 表格化套餐对比
 * 使用 fetchPaymentConfig 正确调用支付配置
 */

import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { getVipPlans, getSiteSettings } from "@/utils/adminApi";
import DeepBlueNavbar from "../components/Navbar";
import DeepBlueFooter from "../components/Footer";

// ==================== 支付配置（参考红色模版实现） ====================
interface PaymentConfig {
  alipay_enabled: boolean;
  wechat_enabled: boolean;
  qrcode_enabled: boolean;
  qrcode_priority: boolean;
  alipay_qrcode: string;
  wechat_qrcode: string;
}

async function fetchPaymentConfig(): Promise<PaymentConfig | null> {
  try {
    const token = localStorage.getItem("admin_token") || localStorage.getItem("token");
    const siteId = localStorage.getItem('current_site_id') || '';
    const res = await fetch("/api/admin/settings/payment", {
      method: "GET",
      headers: {
        "Authorization": token ? `Bearer ${token}` : "",
        'X-Site-Id': siteId,
      },
    });
    const data = await res.json();
    if (data.code === 0 && data.data) {
      return {
        alipay_enabled: !!data.data.alipay_enabled,
        wechat_enabled: !!data.data.wechat_enabled,
        qrcode_enabled: !!data.data.qrcode_enabled,
        qrcode_priority: !!data.data.qrcode_priority,
        alipay_qrcode: data.data.alipay_qrcode || "",
        wechat_qrcode: data.data.wechat_qrcode || "",
      };
    }
    return null;
  } catch (e) {
    console.error("加载支付配置失败:", e);
    return null;
  }
}

// ==================== 类型 ====================
interface VipPlan {
  id: string;
  name: string;
  price: number;
  original_price: number;
  duration: string;
  badge: string;
  features: string[];
  duration_months: number;
  is_recommended?: boolean;
}

const DEFAULT_PLANS: VipPlan[] = [
  { id: "monthly", name: "月度会员", price: 29, original_price: 49, duration: "1个月", badge: "", duration_months: 1, features: ["信息置顶展示 3次/月", "发布信息不限条数", "专属会员标识", "优先客服支持"] },
  { id: "quarterly", name: "季度会员", price: 79, original_price: 147, duration: "3个月", badge: "热门", duration_months: 3, is_recommended: true, features: ["信息置顶展示 10次/季", "发布信息不限条数", "专属会员标识", "优先客服支持", "数据统计分析"] },
  { id: "yearly", name: "年度会员", price: 199, original_price: 588, duration: "12个月", badge: "超值", duration_months: 12, features: ["信息置顶展示 不限次数", "发布信息不限条数", "专属会员标识", "专属客服支持", "数据统计分析", "优先审核通道", "品牌推广曝光"] },
];

const VIP_BENEFITS = [
  { icon: "ri-rocket-line", title: "置顶曝光", desc: "信息优先展示，获得更多浏览量" },
  { icon: "ri-shield-check-line", title: "认证标识", desc: "专属VIP认证，提升企业可信度" },
  { icon: "ri-bar-chart-line", title: "数据分析", desc: "查看浏览、联系等详细统计" },
  { icon: "ri-customer-service-line", title: "专属客服", desc: "优先响应，一对一专属服务" },
  { icon: "ri-infinity-line", title: "无限发布", desc: "不限条数发布服务信息" },
  { icon: "ri-speed-line", title: "极速审核", desc: "信息优先审核，快速上线" },
];

export default function VipPage() {
  const { user } = useAuth();
  const { siteConfig } = useSiteConfig();
  const navigate = useNavigate();
  const siteDisplayName = siteConfig?.site_name || siteConfig?.name || "";

  const [plans, setPlans] = useState<VipPlan[]>(DEFAULT_PLANS);
  const [enableVip, setEnableVip] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState("quarterly");
  const [payMethod, setPayMethod] = useState<"wechat" | "alipay" | "qrcode">("wechat");
  const [showPayModal, setShowPayModal] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [plansRes, settings, payments] = await Promise.all([
          getVipPlans().catch(() => null),
          getSiteSettings().catch(() => null),
          fetchPaymentConfig(),
        ]);

        if (settings) setEnableVip(settings.enable_vip !== false);

        if (plansRes && Array.isArray(plansRes) && plansRes.length > 0) {
          const formatted = plansRes.map((p: any) => ({
            id: p.id, name: p.name || "VIP套餐", price: p.price || p.amount || 0,
            original_price: p.original_price || p.originalPrice || 0,
            duration: `${p.duration_months || p.duration || 1}个月`,
            badge: p.is_recommended ? "推荐" : (p.badge || ""),
            features: p.benefits || p.features || [],
            duration_months: p.duration_months || p.duration || 1,
            is_recommended: p.is_recommended || false,
          }));
          setPlans(formatted);
          if (formatted.length > 0) setSelectedPlan(formatted.find(p => p.is_recommended)?.id || formatted[0].id);
        }

        if (payments) {
          setPaymentConfig(payments);
          if (payments.qrcode_enabled && payments.qrcode_priority) setPayMethod("qrcode");
        }
      } catch (e) {
        console.error("加载VIP数据失败:", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const currentPlan = plans.find(p => p.id === selectedPlan);
  const showAlipay = !!paymentConfig?.alipay_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.alipay_enabled);
  const showWechat = !!paymentConfig?.wechat_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.wechat_enabled);

  useEffect(() => {
    if (payMethod === "wechat" && !showWechat) setPayMethod(showAlipay ? "alipay" : "qrcode");
    else if (payMethod === "alipay" && !showAlipay) setPayMethod(showWechat ? "wechat" : "qrcode");
  }, [showWechat, showAlipay]);

  const handlePay = useCallback(() => {
    if (!user) { navigate("/login", { state: { from: "/vip" } }); return; }
    setShowPayModal(true);
  }, [user, navigate]);

  if (!enableVip && !loading) {
    return (
      <div className="min-h-screen bg-[#0F1D2F]">
        <DeepBlueNavbar />
        <div className="flex items-center justify-center py-24">
          <div className="text-center px-4">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-blue-900/30 flex items-center justify-center">
              <i className="ri-vip-crown-line text-4xl text-blue-400/60" />
            </div>
            <h1 className="text-xl font-bold text-white mb-3">VIP会员功能已关闭</h1>
            <p className="text-blue-300/60 mb-6">非常抱歉，VIP会员功能暂时关闭，如有需要请联系管理员。</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors">
              <i className="ri-arrow-left-line" />返回首页
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F1D2F] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <DeepBlueNavbar />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-[#0F1D2F]">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(#3B82F6 1px, transparent 1px), linear-gradient(90deg, #3B82F6 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/8 rounded-full -translate-y-1/4 translate-x-1/4 blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-24 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-500/20 backdrop-blur-sm rounded-full text-sm text-blue-200 border border-blue-500/20 mb-6">
            <i className="ri-vip-crown-line text-amber-400" />
            <span>VIP 会员</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-white mb-4 leading-tight">
            解锁<span className="text-blue-400">会员权益</span><br className="sm:hidden" />让您的服务脱颖而出
          </h1>
          <p className="text-blue-200/70 text-base sm:text-lg max-w-2xl mx-auto mb-8">
            选择最适合您的会员方案，享受置顶曝光、无限发布、专属认证等特权
          </p>
          <div className="flex flex-wrap justify-center gap-8 sm:gap-12 mt-10 pt-8 border-t border-blue-800/40">
            {[{ num: "10万+", label: "服务信息" }, { num: "5万+", label: "活跃用户" }, { num: "99%", label: "满意度" }].map((s, i) => (
              <div key={i} className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-white">{s.num}</p>
                <p className="text-xs text-blue-300/60 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 权益矩阵 ── */}
      <section className="relative z-10 -mt-10 px-4 sm:px-6 pb-10">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/60 border border-gray-100 p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 text-center mb-8">
              VIP <span className="text-[#0F1D2F]">专属权益</span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {VIP_BENEFITS.map((b) => (
                <div key={b.title} className="group relative overflow-hidden rounded-xl border border-gray-100 bg-white p-5 hover:shadow-md hover:border-blue-200 transition-all duration-300">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-sky-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="w-10 h-10 mb-3 flex items-center justify-center rounded-lg bg-blue-100 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all">
                    <i className={`${b.icon} text-lg`} />
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-1 text-sm">{b.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 套餐选择 ── */}
      <section className="px-4 sm:px-6 pb-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 text-center mb-3">选择适合您的<span className="text-[#0F1D2F]">套餐</span></h2>
          <p className="text-sm text-gray-400 text-center mb-8">选择最适合的方案，解锁更多权益</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {plans.map((plan) => {
              const isSelected = selectedPlan === plan.id;
              return (
                <div key={plan.id} onClick={() => setSelectedPlan(plan.id)}
                  className={`relative cursor-pointer rounded-2xl transition-all duration-300 overflow-hidden border-2 ${
                    isSelected ? "bg-gradient-to-b from-blue-50 to-white border-blue-600 shadow-xl shadow-blue-500/10 scale-[1.02] z-10"
                      : plan.is_recommended ? "bg-white border-blue-200 shadow-md hover:shadow-lg hover:border-blue-400"
                        : "bg-white border-gray-200 hover:border-blue-300 hover:shadow-lg"
                  }`}>
                  {plan.badge && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-20">
                      <span className={`px-4 py-1 rounded-full text-xs font-bold shadow-lg whitespace-nowrap ${isSelected ? "bg-gradient-to-r from-blue-600 to-sky-600 text-white" : "bg-blue-600 text-white"}`}>
                        {plan.badge}
                      </span>
                    </div>
                  )}
                  <div className="p-6 pt-6">
                    <h3 className={`text-center font-bold text-base mb-4 ${isSelected ? "text-blue-700" : "text-gray-800"}`}>{plan.name}</h3>
                    <div className="text-center mb-6">
                      <div className="flex items-baseline justify-center gap-0.5">
                        <span className="text-sm text-gray-400">¥</span>
                        <span className={`text-4xl font-extrabold ${isSelected ? "text-blue-600" : "text-gray-900"}`}>{plan.price}</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">/{plan.duration}</p>
                      {plan.original_price > plan.price && (
                        <p className="text-xs text-gray-400 mt-1.5">
                          <span className="line-through">¥{plan.original_price}</span>
                          <span className="ml-1.5 px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded font-medium text-xs">省¥{plan.original_price - plan.price}</span>
                        </p>
                      )}
                    </div>
                    {isSelected && (
                      <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center shadow-md">
                        <i className="ri-check-line text-white text-xs" />
                      </div>
                    )}
                    <ul className="space-y-3 mb-6">
                      {plan.features.length > 0 ? plan.features.map((f: string, fi: number) => (
                        <li key={fi} className="flex items-start gap-2.5 text-sm">
                          <i className={`mt-0.5 shrink-0 text-base ${isSelected ? "ri-checkbox-circle-fill text-blue-500" : "ri-check-line text-gray-400"}`} />
                          <span className={isSelected ? "text-gray-700" : "text-gray-500"}>{f}</span>
                        </li>
                      )) : <li className="text-sm text-gray-400 text-center">暂未配置权益</li>}
                    </ul>
                    <button onClick={(e) => { e.stopPropagation(); setSelectedPlan(plan.id); }}
                      className={`w-full py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${
                        isSelected ? "bg-gradient-to-r from-blue-700 to-blue-600 text-white shadow-lg shadow-blue-500/30"
                          : "bg-gray-50 text-gray-600 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 hover:border-blue-200"
                      }`}>
                      {isSelected ? "✓ 已选择" : "选择此套餐"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 支付方式 */}
          <div className="mt-8 bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <i className="ri-bank-card-line text-blue-600" />选择支付方式
            </h3>
            <div className="flex flex-wrap gap-3">
              {showWechat && (
                <button onClick={() => setPayMethod("wechat")}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                    payMethod === "wechat" ? "border-green-500 bg-green-50 text-green-700 shadow-sm" : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}>
                  <i className="ri-wechat-pay-fill text-lg" />微信支付
                </button>
              )}
              {showAlipay && (
                <button onClick={() => setPayMethod("alipay")}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                    payMethod === "alipay" ? "border-blue-500 bg-blue-50 text-blue-700 shadow-sm" : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}>
                  <i className="ri-alipay-fill text-lg" />支付宝
                </button>
              )}
            </div>
            {!showAlipay && !showWechat && (
              <p className="text-sm text-gray-400 flex items-center gap-2">
                <i className="ri-information-line" />暂未配置收款方式，请联系管理员
              </p>
            )}
          </div>

          {/* CTA */}
          <div className="mt-6 text-center">
            <button onClick={handlePay}
              className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#0F1D2F] to-[#1a2d45] text-white font-bold rounded-2xl hover:from-[#152a45] hover:to-[#253d5a] transition-all text-base shadow-xl shadow-blue-900/20 hover:shadow-blue-900/40 hover:-translate-y-0.5">
              <i className="ri-vip-crown-line text-xl text-amber-400" />
              <span>立即开通 {currentPlan?.name} — ¥{currentPlan?.price}</span>
            </button>
            <p className="text-xs text-gray-400 mt-3">开通即享全部VIP权益，随时可取消</p>
          </div>
        </div>
      </section>

      <DeepBlueFooter />

      {/* 支付弹窗 */}
      {showPayModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4" onClick={() => setShowPayModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-gray-800 text-lg">扫码支付</h3>
              <button onClick={() => setShowPayModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>
            <div className="text-center">
              {payMethod === "alipay" && showAlipay && (
                <div>
                  <p className="text-xs text-gray-500 mb-3">请使用支付宝扫码付款</p>
                  <img src={paymentConfig?.alipay_qrcode} alt="支付宝收款码" className="w-52 h-52 mx-auto rounded-xl border border-gray-200" />
                </div>
              )}
              {payMethod === "wechat" && showWechat && (
                <div>
                  <p className="text-xs text-gray-500 mb-3">请使用微信扫码付款</p>
                  <img src={paymentConfig?.wechat_qrcode} alt="微信收款码" className="w-52 h-52 mx-auto rounded-xl border border-gray-200" />
                </div>
              )}
              {!showAlipay && !showWechat && (
                <>
                  <div className="w-44 h-44 mx-auto mb-4 bg-gray-100 rounded-xl flex items-center justify-center">
                    <i className="ri-qr-code-line text-5xl text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-600">支付功能暂未启用</p>
                </>
              )}
              <p className="text-2xl font-bold text-[#0F1D2F] mt-4 mb-1">¥{currentPlan?.price}</p>
              <p className="text-xs text-gray-400">{currentPlan?.name} · {currentPlan?.duration}</p>
            </div>
            <div className="mt-5 p-3.5 bg-blue-50 rounded-xl text-xs text-blue-700 text-center font-medium">
              <i className="ri-information-line mr-1" />支付成功后将自动开通VIP权益
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
